/**
 * Whitelabel configuration
 *
 * This module provides domain-based branding configuration for whitelabel deployments.
 * Add new brand configurations here to support additional whitelabel domains.
 */

export type BrandConfig = {
  /** Brand name displayed in UI */
  name: string
  /** Path to the logo displayed in sidebar (small, icon-only) */
  sidebarLogo: string
  /** Optional width for the sidebar logo (px). If omitted, image renders at natural size */
  sidebarLogoWidth?: number
  /** Optional height for the sidebar logo (px). If omitted, image renders at natural size */
  sidebarLogoHeight?: number
  /** Path to the logo displayed on auth/sign-in pages (larger, with text) */
  authLogo: string
  /** Path to the logo for dark backgrounds */
  logoDark: string
  /** Path to the logo for light backgrounds */
  logoLight: string
  /** Path to the blue/colored logo variant */
  logoBlue: string
  /** External website URL for brand link */
  websiteUrl: string
  /** Primary brand color (for custom styling if needed) */
  primaryColor: string
  /** Whether to show marketing content on sign-up page */
  showMarketingContent: boolean
  /** Whether to show the app name text next to the sidebar logo (set false if logo already contains the name) */
  showSidebarAppName: boolean
  /** Custom tagline for sign-up page (if showMarketingContent is true) */
  tagline?: string
}

/**
 * Default Costory/Tsadei branding
 */
const COSTORY_BRAND: BrandConfig = {
  name: "Tsadei",
  sidebarLogo: "/img/logo_only.png",
  sidebarLogoWidth: 54,
  sidebarLogoHeight: 32,
  authLogo: "/img/logo-color.png",
  logoDark: "/img/logo-dark.png",
  logoLight: "/img/logo.png",
  logoBlue: "/img/logo_costory_blue.png",
  websiteUrl: "https://costory.io",
  primaryColor: "#0f3d98",
  showMarketingContent: true,
  showSidebarAppName: true,
  tagline: "FinOps without the FinOps.",
}

/**
 * OptimNow whitelabel branding
 */
const OPTIMNOW_BRAND: BrandConfig = {
  name: "OptimNow",
  sidebarLogo: "https://storage.googleapis.com/ts_screenshots/oem/Optimnow.svg",
  authLogo: "https://storage.googleapis.com/ts_screenshots/oem/Optimnow.svg",
  logoDark: "https://storage.googleapis.com/ts_screenshots/oem/Optimnow.svg",
  logoLight: "https://storage.googleapis.com/ts_screenshots/oem/Optimnow.svg",
  logoBlue: "https://storage.googleapis.com/ts_screenshots/oem/Optimnow.svg",
  websiteUrl: "https://optimnow.io",
  primaryColor: "#2563eb",
  showMarketingContent: false,
  showSidebarAppName: false,
  tagline: "Simple, Smart, and Sustainable Cloud Financial Management",
}

/**
 * Domain to brand mapping
 * Add new whitelabel domains here
 */
const DOMAIN_BRAND_MAP: Record<string, BrandConfig> = {
  // OptimNow domains
  "console.optimnow.io": OPTIMNOW_BRAND,
  "allocation.optimnow.io": OPTIMNOW_BRAND,
  "optimnow.io": OPTIMNOW_BRAND,
  // Add more whitelabel domains as needed:
  // "app.customerbrand.com": CUSTOMER_BRAND,
}

/**
 * Get the brand configuration for the current domain
 */
export function getBrandConfig(): BrandConfig {
  if (typeof window === "undefined") {
    return COSTORY_BRAND
  }

  const hostname = window.location.hostname

  // Check for exact domain match
  if (DOMAIN_BRAND_MAP[hostname]) {
    return DOMAIN_BRAND_MAP[hostname]
  }

  // Check for subdomain match (e.g., staging.console.optimnow.io should still use OptimNow brand)
  for (const [domain, brand] of Object.entries(DOMAIN_BRAND_MAP)) {
    if (hostname.endsWith(`.${domain}`) || hostname === domain) {
      return brand
    }
  }

  // Default to Costory brand
  return COSTORY_BRAND
}

/**
 * React hook to get the current brand configuration
 * This is a simple getter since the domain doesn't change during the session
 */
export function useBrand(): BrandConfig {
  return getBrandConfig()
}

/**
 * Check if the current deployment is a whitelabel (non-Costory) brand
 */
export function isWhitelabel(): boolean {
  const brand = getBrandConfig()
  return brand.name !== "Tsadei"
}
